import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import WorldMapTooltip from './WorldMapTooltip';

const GEO_URL = `${process.env.PUBLIC_URL}/countries-110m.json`;

/**
 * Interpolates between light blue and dark blue based on a ratio (0–1).
 * Used to color geography polygons proportionally to ticket count.
 *
 * @param {number} ratio - Value between 0 and 1 (total / maxTotal)
 * @returns {string} Hex color string
 */
const getCountryFillColor = (ratio) => {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const lightBlue = { r: 0xcf, g: 0xe2, b: 0xf3 }; // #cfe2f3
  const darkBlue = { r: 0x1a, g: 0x3e, b: 0x72 }; // #1a3e72

  const r = Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * clampedRatio);
  const g = Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * clampedRatio);
  const b = Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * clampedRatio);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const WorldMap = ({ countryStatus }) => {
  const [tooltip, setTooltip] = useState({ visible: false, country: '', statusCounts: {}, position: { x: 0, y: 0 } });

  // Build a lookup map from countryStatus keyed by country name, and compute maxTotal for color scaling
  const { countryDataMap, maxTotal } = useMemo(() => {
    if (!countryStatus || countryStatus.length === 0) {
      return { countryDataMap: {}, maxTotal: 0 };
    }
    const map = {};
    let max = 0;
    countryStatus.forEach((c) => {
      map[c.country] = c;
      if ((c.total || 0) > max) {
        max = c.total || 0;
      }
    });
    return { countryDataMap: map, maxTotal: max };
  }, [countryStatus]);

  const handleMouseEnter = useCallback((event, countryData) => {
    const { clientX, clientY } = event;
    setTooltip({
      visible: true,
      country: countryData.country,
      statusCounts: {
        new: countryData.new || 0,
        pendinghr: countryData.pendinghr || 0,
        pendingbtt: countryData.pendingbtt || 0,
        pendingdes: countryData.pendingdes || 0,
        confirmed: countryData.confirmed || 0,
        total: countryData.total || 0,
      },
      position: { x: clientX, y: clientY },
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  if (!countryStatus || countryStatus.length === 0) {
    return <p className="text-center text-muted">No country data available</p>;
  }

  return (
    <div style={{ position: 'relative', width: '100%', maxHeight: '320px', overflow: 'hidden' }}>
      <ComposableMap projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }} style={{ width: '100%', height: 'auto' }}>
        <ZoomableGroup>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const countryName = geo.properties.name;
                const countryData = countryDataMap[countryName];
                const hasData = !!countryData;
                const fillColor =
                  hasData && maxTotal > 0 ? getCountryFillColor((countryData.total || 0) / maxTotal) : '#e0e0e0';

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke="#bbb"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: {
                        fill: hasData ? fillColor : '#ccc',
                        outline: 'none',
                      },
                      pressed: { outline: 'none' },
                    }}
                    {...(hasData
                      ? {
                          onMouseEnter: (e) => handleMouseEnter(e, countryData),
                          onMouseLeave: handleMouseLeave,
                          'aria-label': `${countryData.country}: ${countryData.total} tickets`,
                        }
                      : {})}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      <WorldMapTooltip
        country={tooltip.country}
        statusCounts={tooltip.statusCounts}
        visible={tooltip.visible}
        position={tooltip.position}
      />
    </div>
  );
};

WorldMap.propTypes = {
  countryStatus: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      new: PropTypes.number.isRequired,
      pendinghr: PropTypes.number.isRequired,
      pendingbtt: PropTypes.number.isRequired,
      pendingdes: PropTypes.number.isRequired,
      confirmed: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default WorldMap;
